import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Header from '../components/Header';
import QrCode from '../components/QrCode';

function fmtDate(iso) {
  return new Date(iso).toLocaleString('ru-RU', {
    day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit'
  });
}

function guestRoomUrl(code) {
  return `${window.location.origin}/muzon/room/${code}`;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [newRoom, setNewRoom] = useState(null); // shows freshly created code/djCode
  const [copied, setCopied] = useState('');
  const [qrRoomId, setQrRoomId] = useState(null);

  const fetchRooms = async () => {
    try {
      const data = await api.adminListRooms();
      setRooms(data);
    } catch (e) {
      if (e.message?.includes('401') || e.message?.includes('403')) {
        api.clearSession();
        navigate('/login?role=admin');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRooms(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true); setError('');
    try {
      const room = await api.adminCreateRoom(name.trim());
      setNewRoom(room);
      setName('');
      await fetchRooms();
    } catch (err) { setError(err.message); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id, roomName) => {
    if (!window.confirm(`Удалить комнату "${roomName}"? Все песни будут удалены безвозвратно.`)) return;
    try {
      await api.adminDeleteRoom(id);
      await fetchRooms();
    } catch (err) { setError(err.message); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="app-shell">
      <Header />
      <main className="portal">
        <div className="container">

          <div className="create-room-section">
            <h2>Создать новую комнату</h2>
            <p>Укажите название мероприятия. Будет сгенерирован гостевой код (6 символов) и DJ-код (для входа диджея).</p>
            <form className="create-room-form" onSubmit={handleCreate}>
              <input className="input" type="text" placeholder="Например: Свадьба Анны и Игоря"
                value={name} onChange={e => setName(e.target.value)} maxLength={100} />
              <button className="btn btn-primary" type="submit" disabled={creating || !name.trim()}>
                {creating ? <span className="spinner" /> : '+ Создать'}
              </button>
            </form>
            {error && <div className="alert alert-error" style={{marginTop:12}}>{error}</div>}

            {newRoom && (
              <div className="new-room-result">
                <div className="new-room-grid">
                  <div className="new-room-item">
                    <span className="room-info-label">Гостевой код</span>
                    <div className="code-pill" onClick={() => copy(newRoom.code, 'guest')}>
                      {newRoom.code} {copied==='guest' ? '✓' : '📋'}
                    </div>
                    <div className="qr-block">
                      <QrCode value={guestRoomUrl(newRoom.code)} />
                      <span className="qr-caption">QR-код для гостей</span>
                    </div>
                  </div>
                  <div className="new-room-item">
                    <span className="room-info-label">DJ-код</span>
                    <div className="code-pill accent" onClick={() => copy(newRoom.djCode, 'dj')}>
                      {newRoom.djCode} {copied==='dj' ? '✓' : '📋'}
                    </div>
                  </div>
                </div>
                <p className="room-expires">Действует до: {fmtDate(newRoom.expiresAt)}</p>
              </div>
            )}
          </div>

          <div className="divider" />

          <h2 className="section-title">Все комнаты ({rooms.length})</h2>

          {loading ? (
            <div className="loading-screen" style={{minHeight:120}}><span className="spinner" /></div>
          ) : rooms.length === 0 ? (
            <div className="song-empty">
              <div className="song-empty-icon">🏠</div>
              Пока нет ни одной комнаты.
            </div>
          ) : (
            <div className="room-list">
              {rooms.map(r => (
                <div className="room-row" key={r.id}>
                  <div className="room-row-main">
                    <div className="room-row-name">{r.name}</div>
                    <div className="room-row-meta">
                      <span className="code-pill sm" onClick={() => copy(r.code, `g${r.id}`)}>
                        {r.code} {copied===`g${r.id}` ? '✓':'📋'}
                      </span>
                      <span className="code-pill sm accent" onClick={() => copy(r.djCode, `d${r.id}`)}>
                        DJ: {r.djCode} {copied===`d${r.id}` ? '✓':'📋'}
                      </span>
                    </div>
                    <div className="room-row-stats">
                      🎵 {r.totalSongs} всего · ✓ {r.playedSongs} сыграно · до {fmtDate(r.expiresAt)}
                    </div>
                    {qrRoomId === r.id && (
                      <div className="qr-block">
                        <QrCode value={guestRoomUrl(r.code)} size={140} />
                        <span className="qr-caption">QR-код для гостей</span>
                      </div>
                    )}
                  </div>
                  <div className="room-row-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setQrRoomId(qrRoomId === r.id ? null : r.id)}>
                      {qrRoomId === r.id ? 'Скрыть QR' : 'QR-код'}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/room/${r.code}`)}>
                      Открыть
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(r.id, r.name)} title="Удалить комнату">🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
